import classNames from 'classnames';
import { CSSProperties, useState } from 'react';

import { MenuItemState } from '../../../../../app/layout/MenuItemState';
import { NavigationTranslation } from '../../../../../app/localization/SiteTranslation';
import Icon from '../../../../../app/shared/components/Icon/Icon';
import Link from '../../../../../app/shared/components/Link';
import css from './MegaMenuNav.css';
import {
  NavDropdownBackdrop,
  NavDropdownPanel,
  NavDropdownRoot,
  NavDropdownTrigger,
  NavDropdownViewport,
} from './NavDropdown/NavDropdown';

interface MegaMenuNavProps {
  items: MenuItemState[];
  translation: NavigationTranslation;
  className?: string;
}

export default function MegaMenuNav({ className, items, translation }: MegaMenuNavProps) {
  const [activeDropdownKey, setActiveDropdownKey] = useState<number | null>(null);

  const activeItem = items.find((item) => item.key === activeDropdownKey) ?? null;

  return (
    <nav className={className} aria-label={translation.title}>
      <NavDropdownRoot activeDropdownKey={activeDropdownKey} onActiveDropdownKeyChange={setActiveDropdownKey}>
        <ul className={classNames(css.root, className)}>
          {items.map((item) => (
            <li key={item.key}>
              {item.isGroupingItem ? (
                <NavDropdownTrigger
                  className={classNames(css.item, { [css.active]: item.isActive })}
                  itemKey={item.key}
                >
                  {item.data.name}
                </NavDropdownTrigger>
              ) : (
                <Link
                  className={css.item}
                  href={item.data.url}
                  target={item.data.target}
                  aria-current={item.isActive ? 'page' : undefined}
                >
                  {item.data.name}
                </Link>
              )}
            </li>
          ))}
        </ul>
        <NavDropdownViewport>
          <NavDropdownBackdrop className={css.backdrop} key="backdrop" />
          <NavDropdownPanel className={css.panel} key="panel">
            {activeItem && <MegaMenuNavActiveItemContent item={activeItem} />}
          </NavDropdownPanel>
        </NavDropdownViewport>
      </NavDropdownRoot>
    </nav>
  );
}

// Keep the dropdown within 3 columns; once number of children exceeds 18, rows can grow past 6.
function getGridRows(itemCount: number) {
  const maxPerColumn = 6;
  const minColumns = Math.ceil(itemCount / maxPerColumn);
  const maxColumns = 3;

  return Math.ceil(itemCount / Math.min(maxColumns, minColumns));
}

interface MegaMenuNavActiveItemContentProps {
  item: MenuItemState;
}

function MegaMenuNavActiveItemContent({ item }: MegaMenuNavActiveItemContentProps) {
  const childCount = item.children.length;
  const rows = childCount > 0 ? getGridRows(childCount) : 4;

  return (
    <div className={css.container}>
      <div className={css.inner}>
        <div className={css.intro}>
          <div className={css.title}>{item.data.name}</div>
          {item.data.description && <p className={css.description}>{item.data.description}</p>}
          {item.data.url && (
            <Link className={css.link} href={item.data.url} target={item.data.target}>
              {item.data.name} <Icon className={css.icon} name="arrowNext" />
            </Link>
          )}
        </div>
        {item.isGroupingItem && (
          <ul className={css.items} style={{ '--rows': rows } as CSSProperties}>
            {item.children.map((child) => (
              <li key={child.key}>
                <Link
                  className={css.itemLink}
                  href={child.data.url}
                  target={child.data.target}
                  aria-current={child.isActive ? 'page' : undefined}
                >
                  {child.data.name}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
